// ─── stockoutMath — pace + runway estimate for API-connected products ──────
// Pure helpers. Deliberately no imports from marketplaceUtils so this stays
// unit-testable without pulling in share-recompute logic.
//
// Assumptions:
//   • "API-connected" product means prod.shopifyId or prod.apiStock != null
//   • pace = recentSalesVolume / windowDays (units / day)
//   • Missing pace (zero or no sales) → show em-dash, no forecast
//   • OOS date = today + floor(inStock / pace) days
//
// Shape returned per product:
//   {
//     connected,
//     inStock, pace,
//     soldYesterday,       // prod._salesWindows[1].units
//     soldWindow,          // prod._salesWindows[windowDays].units
//     daysToOOS, oosDate,
//     reason?: "not-connected" | "no-recent-sales"
//   }
// Group shape adds { firstToOOS: { productId, productName } }.

const MS_PER_DAY = 86_400_000;

function toYmd(date) {
  const y = date.getUTCFullYear();
  const m = String(date.getUTCMonth() + 1).padStart(2, "0");
  const d = String(date.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/** Is this product tracked via an API (Shopify, Amazon etc.)?
 *  Kept liberal: if either the shopifyId is set OR an apiStock snapshot
 *  exists, we treat it as connected. */
export function isApiConnected(prod) {
  if (!prod) return false;
  return !!(prod.shopifyId || prod.apiStock != null);
}

function n(v) { return v == null ? 0 : Number(v) || 0; }

/** Read units from prod._salesWindows[bucket]. Returns null when the product
 *  isn't API-connected (so the UI can show an em-dash for truly missing data)
 *  and 0 when the product IS connected but sold nothing in that bucket
 *  (meaningful zero). */
function windowUnits(prod, bucket) {
  if (!prod || !isApiConnected(prod)) return null;
  const w = prod._salesWindows;
  if (!w || typeof w !== "object") return null;
  const entry = w[bucket];
  if (!entry) return null; // bucket not populated yet (e.g. pre-resync)
  const v = Number(entry.units);
  return Number.isFinite(v) ? v : 0;
}

/** Compute stockout info for a single product.
 *  windowDays — the current Sales-Window selector value (7|14|30|60|90). */
export function productStockoutInfo(prod, windowDays, todayDate = new Date()) {
  const win = Math.max(1, Number(windowDays) || 30);

  if (!prod) {
    return {
      connected: false, inStock: null, pace: null,
      soldYesterday: null, soldWindow: null,
      daysToOOS: null, oosDate: null,
    };
  }
  const connected = isApiConnected(prod);
  if (!connected) {
    return {
      connected: false, inStock: null, pace: null,
      soldYesterday: null, soldWindow: null,
      daysToOOS: null, oosDate: null,
      reason: "not-connected",
    };
  }

  const inStock = prod.apiStock != null ? n(prod.apiStock) : n(prod.currentStock);
  const recent = n(prod.recentSalesVolume);
  const pace = recent > 0 ? recent / win : 0;

  const soldYesterday = windowUnits(prod, 1);
  const soldWindow    = windowUnits(prod, win);

  if (pace <= 0) {
    return {
      connected, inStock, pace: 0,
      soldYesterday, soldWindow,
      daysToOOS: null, oosDate: null,
      reason: "no-recent-sales",
    };
  }

  const daysToOOS = Math.max(0, Math.floor(inStock / pace));
  const oosMs = todayDate.getTime() + daysToOOS * MS_PER_DAY;
  const oosDate = toYmd(new Date(oosMs));

  return {
    connected, inStock, pace,
    soldYesterday, soldWindow,
    daysToOOS, oosDate,
  };
}

/** Aggregate stockout info for a group of products. `productsInGroup` is the
 *  already-resolved array (caller joined group.productIds → marketplace products). */
export function groupStockoutInfo(productsInGroup, windowDays, todayDate = new Date()) {
  const empty = {
    connected: false, inStock: null, pace: null,
    soldYesterday: null, soldWindow: null,
    daysToOOS: null, oosDate: null, firstToOOS: null,
  };
  if (!Array.isArray(productsInGroup) || !productsInGroup.length) return empty;

  const members = productsInGroup
    .map(p => ({ prod: p, info: productStockoutInfo(p, windowDays, todayDate) }))
    .filter(m => m.info.connected);
  if (!members.length) return empty;

  const inStock = members.reduce((s, m) => s + n(m.info.inStock), 0);

  // Sum yesterday + selected window totals across members. If any member is
  // missing a bucket, skip it (don't let one missing value poison the sum).
  let yday = 0;
  let ydayAny = false;
  let win = 0;
  let winAny = false;
  for (const m of members) {
    if (m.info.soldYesterday != null) { yday += m.info.soldYesterday; ydayAny = true; }
    if (m.info.soldWindow != null)    { win  += m.info.soldWindow;    winAny  = true; }
  }

  // Weighted avg pace by recentSalesVolume — falls back to simple mean if
  // weights all zero (no product has recent sales).
  let weightSum = 0;
  let weightedPace = 0;
  for (const m of members) {
    const w = n(m.prod.recentSalesVolume);
    weightSum += w;
    weightedPace += w * n(m.info.pace);
  }
  const pace = weightSum > 0
    ? weightedPace / weightSum
    : (members.reduce((s, m) => s + n(m.info.pace), 0) / members.length);

  // Find the first member to go OOS — min non-null oosDate
  let firstToOOS = null;
  for (const m of members) {
    if (!m.info.oosDate) continue;
    if (!firstToOOS || m.info.oosDate < firstToOOS.info.oosDate) firstToOOS = m;
  }

  const base = {
    connected: true,
    inStock,
    pace,
    soldYesterday: ydayAny ? yday : null,
    soldWindow:    winAny  ? win  : null,
  };

  if (!firstToOOS) {
    return { ...base, daysToOOS: null, oosDate: null, firstToOOS: null };
  }

  return {
    ...base,
    daysToOOS: firstToOOS.info.daysToOOS,
    oosDate: firstToOOS.info.oosDate,
    firstToOOS: { productId: firstToOOS.prod.id, productName: firstToOOS.prod.name || firstToOOS.prod.id },
  };
}

/**
 * Severity bucket for the days-to-stockout number.
 * Returns one of: "none" | "critical" | "stale" | "warning" | "ok".
 *
 * Both "critical" and "stale" paint red in the UI:
 *   • critical → daysToOOS ≤ leadTime (can't reorder in time)
 *   • stale    → daysToOOS absurdly large (low-confidence forecast — the
 *                pace is so low the number isn't trustworthy)
 * warning → within leadTime + 21 days (order soon zone)
 * ok      → plenty of runway
 *
 * @param {number|null} daysToOOS
 * @param {number}      leadTimeDays  — defaults to 14 when unknown
 * @param {number}      staleAfter    — days above which the estimate is junk (default 365)
 */
export function stockoutSeverity(daysToOOS, leadTimeDays = 14, staleAfter = 365) {
  if (daysToOOS == null) return "none";
  const lt = Math.max(0, Number(leadTimeDays) || 0);
  if (daysToOOS <= lt) return "critical";
  if (daysToOOS > staleAfter) return "stale";
  if (daysToOOS <= lt + 21) return "warning";
  return "ok";
}
